import React from 'react';
import { fmtInt, fmtPct } from './format';

function Kpi({ label, value, hint, children }) {
  return (
    <div className="rounded-lg border bg-white px-4 py-3 min-w-0">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      {children || (
        <>
          <div className="mt-1 text-xl font-semibold text-gray-900 tabular-nums truncate">{value}</div>
          {hint ? <div className="mt-0.5 text-xs text-gray-500 truncate">{hint}</div> : null}
        </>
      )}
    </div>
  );
}

/** Top categories as a ranked mini-list with relative bars — wraps instead of truncating. */
function DistinctiveCategories({ categories }) {
  const top = (categories || []).filter((c) => c.distinctiveness != null).slice(0, 3);
  if (!top.length) return <div className="mt-1 text-xl font-semibold text-gray-900">—</div>;
  const max = Math.max(1, ...top.map((c) => c.distinctiveness));
  return (
    <ol className="mt-1.5 space-y-1">
      {top.map((c, idx) => (
        <li key={c.id} className="text-xs text-gray-800">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">
              <span className="text-gray-400 tabular-nums mr-1">{idx + 1}.</span>
              {c.label}
            </span>
            <span className="text-gray-400 tabular-nums shrink-0" title="Distinctiveness (pts of share that differ)">{c.distinctiveness.toFixed(0)}</span>
          </div>
          <div className="mt-0.5 h-1 rounded bg-gray-100 overflow-hidden">
            <div className="h-full rounded bg-[#0062FB]/70" style={{ width: `${Math.max(4, Math.round((100 * c.distinctiveness) / max))}%` }} />
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function KpiStrip({ overview, loading, focus, flavor }) {
  const k = overview?.kpis || {};
  // On a clickers-focused report there is no published converter distribution,
  // so a converter count and a conversion rate would both be made up.
  const clickersFocus = focus === 'clickers';
  // The flavor rides in on the overview, so it is unknown on the first paint.
  // Saying anything about converters before it lands means printing a message
  // that has to be corrected a moment later.
  const noConverterCounts = flavor === 'general' || flavor === 'vertical';
  const convertersHint = (() => {
    if (k.converters != null) return k.clickers != null ? `${fmtInt(k.clickers)} clickers` : null;
    if (!flavor) return null;
    return noConverterCounts ? 'No converter data for this offer' : 'counts not loaded yet';
  })();
  const convRateHint = (() => {
    if (k.conv_rate != null) return 'converters ÷ clickers';
    if (!flavor) return null;
    return noConverterCounts ? 'No conversion rate data for this offer' : 'converters ÷ clickers';
  })();
  return (
    <div
      className={`grid grid-cols-2 gap-3 ${clickersFocus ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}
      aria-busy={loading}
    >
      {clickersFocus ? (
        <Kpi
          label="Clickers"
          value={k.clickers != null ? fmtInt(k.clickers) : '—'}
          hint="converter data is not published for this offer"
        />
      ) : (
        <>
          <Kpi
            label="Converters"
            value={k.converters != null ? fmtInt(k.converters) : '—'}
            hint={convertersHint}
          />
          <Kpi
            label="Conversion rate"
            value={k.conv_rate != null ? fmtPct(k.conv_rate, 1) : '—'}
            hint={convRateHint}
          />
        </>
      )}
      <Kpi
        label="Profile coverage"
        value={k.profile_coverage != null ? fmtPct(k.profile_coverage) : '—'}
        hint={`avg. fill rate · ${k.n_features || 0} features · ${k.n_significant_findings || 0} ★ findings`}
      />
      <Kpi label="Most distinctive categories">
        <DistinctiveCategories categories={overview?.categories} />
      </Kpi>
    </div>
  );
}
